import { fetchWithXsrf, getAntiForgeryToken } from "@siisel/frontend-shared";
import { useState } from "react";
import type { CacheSettingsFormViewModel } from "../types";

function appendBoolean(formData: FormData, name: string, value: boolean) {
  formData.append(name, value ? "true" : "false");
}

type BooleanFieldProps = {
  id: string;
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: (value: boolean) => void;
};

function BooleanField({ id, label, checked, disabled, onChange }: BooleanFieldProps) {
  return (
    <div className="form-group forms-app-toggle-row">
      <label htmlFor={id}>{label}</label>
      <label className="forms-app-toggle">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.currentTarget.checked)}
        />
        <span>{checked ? "Ano" : "Ne"}</span>
      </label>
    </div>
  );
}

export default function CacheSettingsFormApp(props: CacheSettingsFormViewModel) {
  const [values, setValues] = useState(props.values);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  function setValue(name: keyof CacheSettingsFormViewModel["values"], value: boolean) {
    setValues((current) => ({ ...current, [name]: value }));
    setErrorMessage(null);
  }

  async function submit(root: HTMLElement | null) {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const formData = new FormData();
      const token = getAntiForgeryToken(root);
      if (token) {
        formData.append("__RequestVerificationToken", token);
      }

      appendBoolean(formData, "IsCacheEnabled", values.isCacheEnabled);
      appendBoolean(formData, "UseReadonlyGisDataProvider", values.useReadonlyGisDataProvider);
      appendBoolean(formData, "UseReadonlyChartDataProvider", values.useReadonlyChartDataProvider);
      appendBoolean(formData, "UseGisFeImplementation", values.useGisFeImplementation);
      appendBoolean(formData, "UseChartFeImplementation", values.useChartFeImplementation);
      formData.append("saveAndClose", "false");

      const response = await fetchWithXsrf(props.submitUrl, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "text/html"
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      window.location.reload();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Ulozeni se nepodarilo.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="forms-app cache-settings-form-app">
      {errorMessage ? <div className="alert alert-danger">{errorMessage}</div> : null}
      <div className="base-form">
        <div className="inner-form">
          <BooleanField
            id="cache-is-enabled"
            label={props.labels.isCacheEnabled}
            checked={values.isCacheEnabled}
            disabled={isSubmitting}
            onChange={(value) => setValue("isCacheEnabled", value)}
          />
          <BooleanField
            id="cache-readonly-gis"
            label={props.labels.useReadonlyGisDataProvider}
            checked={values.useReadonlyGisDataProvider}
            disabled={isSubmitting}
            onChange={(value) => setValue("useReadonlyGisDataProvider", value)}
          />
          <BooleanField
            id="cache-readonly-chart"
            label={props.labels.useReadonlyChartDataProvider}
            checked={values.useReadonlyChartDataProvider}
            disabled={isSubmitting}
            onChange={(value) => setValue("useReadonlyChartDataProvider", value)}
          />
          <BooleanField
            id="cache-gis-fe"
            label={props.labels.useGisFeImplementation}
            checked={values.useGisFeImplementation}
            disabled={isSubmitting}
            onChange={(value) => setValue("useGisFeImplementation", value)}
          />
          <BooleanField
            id="cache-chart-fe"
            label={props.labels.useChartFeImplementation}
            checked={values.useChartFeImplementation}
            disabled={isSubmitting}
            onChange={(value) => setValue("useChartFeImplementation", value)}
          />
        </div>
      </div>

      <div className="panel-footer">
        <button
          type="button"
          className="btn btn-success btn-sm"
          disabled={isSubmitting}
          onClick={(event) => void submit(event.currentTarget.closest(".forms-app"))}
        >
          <em className="fa fa-save"></em>
          {isSubmitting ? `${props.submitText}...` : props.submitText}
        </button>
      </div>
    </div>
  );
}
